/**
 * Pure filter helpers for the Live Map header filters (Region, Local/OTR).
 * Kept free of React so they can be unit tested in isolation.
 */

export type DriverType = 'LOCAL' | 'OTR';

/** Sentinel option value for vehicles whose dispatcher has no region. */
export const NO_REGION_VALUE = '__NO_REGION__';

export interface RegionOption {
  value: string;
  label: string;
}

const toTitleCase = (value: string): string =>
  value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Build the Region dropdown options from the regions present on the loaded vehicles.
 * Values are uppercased so matching is stable regardless of source casing.
 * A "No Region" bucket is appended only when at least one vehicle lacks a region.
 */
export const buildRegionOptions = (regions: Array<string | null | undefined>): RegionOption[] => {
  const distinct = new Set<string>();
  let hasMissing = false;
  for (const region of regions) {
    const normalized = (region || '').trim().toUpperCase();
    if (normalized) {
      distinct.add(normalized);
    } else {
      hasMissing = true;
    }
  }
  const options = Array.from(distinct)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: toTitleCase(value) }));
  if (hasMissing && regions.length > 0) {
    options.push({ value: NO_REGION_VALUE, label: 'No Region' });
  }
  return options;
};

/** True when no regions are selected, or the vehicle's region is one of the selected ones. */
export const matchesRegion = (
  vehicleRegion: string | null | undefined,
  selectedRegions: string[],
): boolean => {
  if (selectedRegions.length === 0) return true;
  const normalized = (vehicleRegion || '').trim().toUpperCase();
  if (!normalized) return selectedRegions.includes(NO_REGION_VALUE);
  return selectedRegions.includes(normalized);
};

/**
 * True when no type (or both types) is selected, or the driver's preference matches.
 * Drivers with no recorded preference are hidden when a single type is selected.
 */
export const matchesDriverType = (
  preference: string | null | undefined,
  selectedTypes: DriverType[],
): boolean => {
  if (selectedTypes.length === 0 || selectedTypes.length >= 2) return true;
  const normalized = (preference || '').trim().toUpperCase();
  return selectedTypes.includes(normalized as DriverType);
};
